import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  CalendarIcon,
  EyeIcon,
  XMarkIcon,
  TruckIcon,
  MapPinIcon,
  ClockIcon,
  PhotoIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';
import toast from '../utils/toast';
import jsPDF from 'jspdf';

const IndiaPost = () => {
  const [consignments, setConsignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Add consignment states
  const [showAddModal, setShowAddModal] = useState(false);
  const [newConsignment, setNewConsignment] = useState({
    consignmentId: ''
  });
  const [adding, setAdding] = useState(false);
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  
  // Detail modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedConsignment, setSelectedConsignment] = useState(null);
  const [scrapedData, setScrapedData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Image modal states
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState('');
  const [pdfGenerating, setPdfGenerating] = useState(false);

  useEffect(() => {
    fetchConsignments();
  }, []);

  const fetchConsignments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/india-post/consignments');
      if (response.data.success) {
        setConsignments(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching consignments:', error);
      setError('Failed to fetch consignments');
      toast.error('Failed to fetch consignments');
    } finally {
      setLoading(false);
    }
  };

  const handleAddConsignment = async (e) => {
    e.preventDefault();
    
    if (!newConsignment.consignmentId.trim()) {
      setError('Consignment ID is required');
      return;
    }

    try {
      setAdding(true);
      setError('');
      
      const response = await api.post('/india-post/consignments', newConsignment);
      
      if (response.data.success) {
        setSuccess('Consignment added successfully!');
        toast.success('Consignment added successfully!');
        setShowAddModal(false);
        setNewConsignment({
          consignmentId: ''
        });
        fetchConsignments();
      }
    } catch (error) {
      console.error('Error adding consignment:', error);
      const errorMessage = error.response?.data?.message || 'Failed to add consignment';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteConsignment = async (consignmentId) => {
    if (!window.confirm('Are you sure you want to delete this consignment?')) {
      return;
    }

    try {
      await api.delete(`/india-post/consignments/${consignmentId}`);
      setSuccess('Consignment deleted successfully!');
      toast.success('Consignment deleted successfully!');
      fetchConsignments();
    } catch (error) {
      console.error('Error deleting consignment:', error);
      setError('Failed to delete consignment');
      toast.error('Failed to delete consignment');
    }
  };

  const handleViewDetail = async (consignment) => {
    try {
      setLoadingDetail(true);
      setSelectedConsignment(consignment);
      setShowDetailModal(true);
      
      const response = await api.get(`/india-post/scraped-data/${consignment.consignmentId}`);
      
      if (response.data.success) {
        setScrapedData(response.data.data);
      } else {
        setError('No tracking data found for this consignment');
        toast.error('No tracking data found for this consignment');
      }
    } catch (error) {
      console.error('Error fetching scraped data:', error);
      const errorMessage = error.response?.data?.message || 'Failed to fetch tracking data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleViewImage = async (consignmentId) => {
    console.log('🖼️ handleViewImage called with consignmentId:', consignmentId);
    console.log('🖼️ selectedConsignment:', selectedConsignment);
    
    if (!consignmentId) {
      console.error('❌ No consignmentId provided to handleViewImage');
      return;
    }
    
    console.log('🖼️ Opening image for consignment:', consignmentId);
    setImageLoading(true);
    setImageError(false);
    setShowImageModal(true);
    
    // Set the image source
    const imageUrl = `/api/india-post/image/${consignmentId}?dc=${Math.random()}`;
    console.log('🖼️ Image URL:', imageUrl);
    setImageSrc(imageUrl);
    
    // Test the API endpoint first
    try {
      console.log('🖼️ Testing API endpoint...');
      const response = await api.get(`/india-post/image/${consignmentId}?dc=${Math.random()}`, {
        responseType: 'blob' // Important for binary data
      });
      
      console.log('✅ API call successful:', response.status);
      console.log('✅ Content-Type:', response.headers['content-type']);
      console.log('✅ Content-Length:', response.headers['content-length']);
      
      // Create object URL for the blob
      const blob = response.data;
      const objectUrl = URL.createObjectURL(blob);
      setImageSrc(objectUrl);
      setImageLoading(false);
      setImageError(false);
      
    } catch (error) {
      console.error('❌ API call failed:', error);
      setImageLoading(false);
      setImageError(true);
    }
  };

  const handlePrintToPDF = async () => {
    if (!selectedConsignment?.consignmentId || !imageSrc) {
      toast.error('No image available to print');
      return;
    }

    setPdfGenerating(true);
    
    try {
      console.log('📄 Generating PDF for consignment:', selectedConsignment.consignmentId);
      
      // Convert image to base64 if it's a blob URL
      let imageData;
      if (imageSrc.startsWith('blob:')) {
        // Fetch the blob and convert to base64
        const response = await fetch(imageSrc);
        const blob = await response.blob();
        imageData = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } else {
        imageData = imageSrc;
      }
      
      // Get original image dimensions
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageData;
      });
      
      const originalWidth = img.width;
      const originalHeight = img.height;
      console.log('📐 Original image dimensions:', originalWidth, 'x', originalHeight);
      
      // Create new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate margins and available space
      const margin = 20; // 20mm margin on all sides
      const headerHeight = 60; // Space for header (first page only)
      const availableWidth = pageWidth - (margin * 2);
      const availableHeight = pageHeight - (margin * 2); // Full page height for all pages
      
      // Calculate scale to fit width while maintaining aspect ratio
      const scale = availableWidth / originalWidth;
      const scaledWidth = originalWidth * scale;
      const scaledHeight = originalHeight * scale;
      
      console.log('📐 Scaled dimensions:', scaledWidth, 'x', scaledHeight);
      console.log('📐 Available space:', availableWidth, 'x', availableHeight);
      
      // Calculate how many pages we need
      const pagesNeeded = Math.ceil(scaledHeight / availableHeight);
      console.log('📄 Pages needed:', pagesNeeded);
      
      // Add header to first page only
      addHeaderToPage(pdf, selectedConsignment.consignmentId, pageWidth);
      
      // Split image across pages
      for (let page = 0; page < pagesNeeded; page++) {
        if (page > 0) {
          pdf.addPage();
          // No header for subsequent pages
        }
        
        // Calculate the portion of image to show on this page
        const startY = page * availableHeight;
        const endY = Math.min((page + 1) * availableHeight, scaledHeight);
        const visibleHeight = endY - startY;
        
        // Calculate source coordinates for this page
        const sourceY = (startY / scaledHeight) * originalHeight;
        const sourceHeight = (visibleHeight / scaledHeight) * originalHeight;
        
        console.log(`📄 Page ${page + 1}: Showing Y ${startY} to ${endY} (height: ${visibleHeight})`);
        console.log(`📄 Source Y: ${sourceY} to ${sourceY + sourceHeight} (height: ${sourceHeight})`);
        
        // Add image portion to this page
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = originalWidth;
        canvas.height = sourceHeight;
        
        // Draw the portion of the image
        ctx.drawImage(
          img,
          0, sourceY, originalWidth, sourceHeight, // Source rectangle
          0, 0, originalWidth, sourceHeight // Destination rectangle
        );
        
        // Convert canvas to image data
        const pageImageData = canvas.toDataURL('image/png');
        
        // Add to PDF
        const imgY = page === 0 ? headerHeight : margin; // Use header space only on first page
        pdf.addImage(pageImageData, 'PNG', margin, imgY, scaledWidth, visibleHeight);
      }
      
      // Save the PDF
      const fileName = `IndiaPost_${selectedConsignment.consignmentId}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      toast.success(`PDF generated successfully! (${pagesNeeded} page${pagesNeeded > 1 ? 's' : ''})`);
      console.log('✅ PDF generated:', fileName, `(${pagesNeeded} pages)`);
      
    } catch (error) {
      console.error('❌ Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setPdfGenerating(false);
    }
  };

  // Helper function to add header to first page only
  const addHeaderToPage = (pdf, consignmentId, pageWidth) => {
    // Add title
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('India Post Consignment', pageWidth / 2, 20, { align: 'center' });
    
    // Add consignment ID
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Consignment ID: ${consignmentId}`, pageWidth / 2, 35, { align: 'center' });
    
    // Add date
    const currentDate = new Date().toLocaleDateString();
    pdf.setFontSize(10);
    pdf.text(`Generated on: ${currentDate}`, pageWidth / 2, 45, { align: 'center' });
    
    // Add a line separator
    pdf.setLineWidth(0.5);
    pdf.line(20, 50, pageWidth - 20, 50);
  };


  const filteredConsignments = consignments.filter(consignment => {
    const matchesSearch = 
      consignment.consignmentId.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">India Post Consignments</h1>
        <p className="text-gray-600">Manage your India Post consignment tracking</p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
            <button onClick={clearMessages} className="ml-auto text-green-400 hover:text-green-600">
              ×
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button onClick={clearMessages} className="ml-auto text-red-400 hover:text-red-600">
              ×
            </button>
          </div>
        </div>
      )}

      {/* Add Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Consignment
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search consignment IDs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
          />
        </div>
      </div>

      {/* Consignments List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading consignments...</p>
        </div>
      ) : filteredConsignments.length === 0 ? (
        <div className="text-center py-8">
          <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No consignments found</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-2 text-blue-600 hover:text-blue-800"
          >
            Add your first consignment
          </button>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredConsignments.map((consignment) => (
              <li key={consignment._id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-lg font-medium text-blue-600">
                          {consignment.consignmentId}
                        </p>
                        <div className="ml-2 flex-shrink-0 flex space-x-2">
                          <button
                            onClick={() => handleViewDetail(consignment)}
                            className="text-blue-400 hover:text-blue-600"
                            title="View tracking details"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteConsignment(consignment.consignmentId)}
                            className="text-red-400 hover:text-red-600"
                            title="Delete consignment"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-2">
                        <div className="flex items-center text-sm text-gray-500">
                          <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                          <p>Added: {new Date(consignment.createdAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Add Consignment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Consignment</h3>
              
              <form onSubmit={handleAddConsignment}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Consignment ID *
                  </label>
                  <input
                    type="text"
                    required
                    value={newConsignment.consignmentId}
                    onChange={(e) => setNewConsignment(prev => ({ ...prev, consignmentId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter consignment ID"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Enter the India Post consignment tracking number
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={adding}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {adding ? 'Adding...' : 'Add Consignment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Tracking Details - {selectedConsignment?.consignmentId}
                </h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      console.log('🖼️ Photo button clicked!');
                      console.log('🖼️ selectedConsignment:', selectedConsignment);
                      console.log('🖼️ consignmentId:', selectedConsignment?.consignmentId);
                      handleViewImage(selectedConsignment?.consignmentId);
                    }}
                    className="text-blue-400 hover:text-blue-600"
                    title="View consignment image"
                  >
                    <PhotoIcon className="h-6 w-6" />
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setSelectedConsignment(null);
                      setScrapedData(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              {loadingDetail ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading tracking data...</p>
                </div>
              ) : scrapedData ? (
                <div className="space-y-6">
                  {/* Consignment Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                      <DocumentTextIcon className="h-5 w-5 mr-2" />
                      Consignment Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(scrapedData.consignmentInfo).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="font-medium text-gray-700">{key}:</span>
                          <span className="text-gray-900">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Status */}
                  {scrapedData.status && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                        <TruckIcon className="h-5 w-5 mr-2" />
                        Delivery Status
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        {Object.entries(scrapedData.status).map(([status, count]) => (
                          <div key={status} className={`p-2 rounded text-center ${
                            count > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
                          }`}>
                            <div className="font-medium">{status}</div>
                            <div className="text-sm">{count > 0 ? '✓' : '○'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Routing Steps */}
                  {scrapedData.routingSteps && scrapedData.routingSteps.length > 0 && (
                    <div className="bg-white border border-gray-200 p-4 rounded-lg">
                      <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                        <MapPinIcon className="h-5 w-5 mr-2" />
                        Tracking History
                      </h4>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {scrapedData.routingSteps.map((step, index) => (
                          <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <ClockIcon className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-600">{step.dateTime}</span>
                              </div>
                              <p className="text-sm font-medium text-gray-900">{step.event}</p>
                              <p className="text-sm text-gray-600">{step.office}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Scraped Info */}
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h4 className="text-md font-semibold text-gray-900 mb-2">Data Information</h4>
                    <div className="text-sm text-gray-600">
                      <p><strong>Scraped At:</strong> {new Date(scrapedData.scrapedAt).toLocaleString()}</p>
                      <p><strong>Success:</strong> {scrapedData.success ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No tracking data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Consignment Image - {selectedConsignment?.consignmentId}
                </h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePrintToPDF}
                    className="text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 px-3 py-2 rounded-md flex items-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Print to PDF"
                    disabled={imageLoading || imageError || !imageSrc || pdfGenerating}
                  >
                    {pdfGenerating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                        <span className="text-sm font-medium">Generating...</span>
                      </>
                    ) : (
                      <>
                        <PrinterIcon className="h-5 w-5" />
                        <span className="text-sm font-medium">Print PDF</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      // Clean up object URL if it exists
                      if (imageSrc && imageSrc.startsWith('blob:')) {
                        URL.revokeObjectURL(imageSrc);
                      }
                      setShowImageModal(false);
                      setImageLoading(false);
                      setImageError(false);
                      setImageSrc('');
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              <div className="text-center">
                {imageLoading && (
                  <div className="py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading image...</p>
                  </div>
                )}
                
                {!imageLoading && !imageError && imageSrc && (
                  <div className="relative">
                    <img
                      src={imageSrc}
                      alt={`Consignment ${selectedConsignment?.consignmentId}`}
                      className="max-w-full h-auto mx-auto rounded-lg shadow-lg"
                      style={{ maxHeight: '70vh' }}
                    />
                  </div>
                )}
                
                {imageError && (
                  <div className="py-8">
                    <PhotoIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">Image not found</p>
                    <p className="text-sm text-gray-500">
                      No image available for consignment {selectedConsignment?.consignmentId}
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      API endpoint: /api/india-post/image/{selectedConsignment?.consignmentId}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndiaPost;
